import classes from './AuctionContent.module.scss';
import React, {useState, useCallback, useEffect, useContext, useRef} from 'react';
import FilterAccordion from './FilterAccordion';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { carSearch, getCarFilters, updateCarFavourite } from '../../services/index';
import { setFilterQuery } from '../../store/reducers/filterReducer';
import { setCarDetail, updateCarDetail } from '../../store/reducers/carReducer';
import { useNavigate } from 'react-router-dom';
import { setDesktopFilterOpen, setMobileFilterOpen } from '../../store/reducers/filterReducer';
import { setSearchQuery } from '../../store/reducers/searchSlice';
import { setDay, setSites } from '../../store/reducers/scheduleReducer';
import EmptyContentCard from '../Layout/EmptyContentCard';
import { useSnackbar } from 'notistack';

import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import AuthContext from "../../store/auth-context";
import Loading from '../Layout/Loading';
import { formatDate, formatDateWithTime, createSortableTimestamp } from '../../helper/utils';
import { EXCLUDED_MAKERS, SEARCH_MAX_LENGTH } from '../../config/constants';
import { Button, Tooltip } from '@chakra-ui/react';

// Import the new components
import MobileAuctionContent from './MobileAuctionContent';
import DesktopAuctionContent from './DesktopAuctionContent';

const muiTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
  },
  components: {
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          border: '1px solid #e0e0e0',
          minWidth: '300px',
          padding: 0,
          marginTop: '8px'
        }
      }
    }
  }
});

const AuctionContent = () => {
    const dispatch = useDispatch();
    const isMobileFilterOpen = useSelector((state) => state.filter.isMobileFilterOpen); 
    const isDesktopFilterOpen = useSelector((state) => state.filter.isDesktopFilterOpen); 
    const searchQuery = useSelector((state) => state.search.query);
    const isSearched = useSelector((state) => state.search.isSearched);
    const filterQuery = useSelector((state) => state.filter.query);
    const scheduleDay = useSelector((state) => state.schedule.day);
    const scheduleSite = useSelector((state) => state.schedule.sites);
    const isMobile = useMediaQuery('(max-width:768px)');
    const [page, setPage] = useState(1);
    const [searchString, setSearchString] = useState(searchQuery ? searchQuery : "");
    const [filterString, setFilterString] = useState(filterQuery ? filterQuery : "");
    const [carsData, setCarsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const totalCarsToShow = 9; // Changed from state to constant since it's not being updated
    const [totalCars, setTotalCars] = useState(0);
    const [carFiltersList, setCarFiltersList] = useState({});
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [savedSearchFilters, setSavedSearchFilters] = useState([]);
    const [selectedSavedSearchFilter, setSelectedSavedSearchFilter] = useState({name:'',filter:''});
    const {userData, isFreeTier = true} = useContext(AuthContext);
    const [localFavorites, setLocalFavorites] = useState(new Set());

    // Add new state for initial loading
    const [initialLoading, setInitialLoading] = useState(true);
    const [sortBy, setSortBy] = useState("default");
    const [initialCarsData, setInitialCarsData] = useState([]);
    const scrollContainerRef = useRef(null)
    // Tracks the most recent car-search request so stale (out-of-order) responses
    // from prior pages/filters can be ignored instead of overwriting/appending onto
    // the current results.
    const latestRequestId = useRef(0);
    
    // Add view mode state
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

    // Add new state for page size
    const [pageSize, setPageSize] = useState(9); // Initial load size is 9
    
    // Add new state for filter animation
    const [filterAnimationState, setFilterAnimationState] = useState(''); // '', 'opening', or 'closing'
    
    // Handle view mode toggle
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        // Reset scroll position when changing view mode
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
            // Also reset any scroll-related flags to ensure clean state
            scrollContainerRef.current.isLoadingScheduled = false;
            scrollContainerRef.current.lastScrollTop = 0;
        }
        
        // When changing view mode, we need to maintain the current page
        // but reset the loading state to prevent issues
        setLoading(false);
        
        // Use setTimeout to ensure the DOM has updated with the new view mode
        setTimeout(() => {
            // Force a reflow/repaint to ensure scrollbar is activated
            if (scrollContainerRef.current) {
                // This forces a reflow/recalculation of layout
                const forceReflow = scrollContainerRef.current.offsetHeight;
                
                // Force the scrollbar to be visible
                if (mode === 'list') {
                    scrollContainerRef.current.style.overflowY = 'scroll';
                }
                
                // Add a small amount of scrollable content if needed
                const currentHeight = scrollContainerRef.current.scrollHeight;
                const viewportHeight = scrollContainerRef.current.clientHeight;
                
                console.log('Scroll container dimensions after view change:', {
                    mode,
                    scrollHeight: currentHeight,
                    clientHeight: viewportHeight,
                    hasOverflow: currentHeight > viewportHeight
                });
                
                // Force a scroll event to initialize the scrolling behavior
                const event = {
                    currentTarget: scrollContainerRef.current
                };
                handleScroll(event);
            }
        }, 100);
    };

    useEffect(() => {
      setSearchString(searchQuery);
      if(scheduleSite.length > 0){
        setSearchString(`${//append array of sites
          scheduleSite.map((site) => {
            return site + " ";
          }
        )}`);
      }
    }, [searchQuery,scheduleSite]);


    useEffect(() => {
                scrollContainerRef.current.scrollTop = 0;
            setPage(1);
    }, [isSearched]);


    useEffect(() => {
      if(scheduleDay){
            setFilterString(`&filter[bidDate]=["${scheduleDay}"]`);
        }
      if(filterQuery){
            setFilterString(filterQuery);
                scrollContainerRef.current.scrollTop = 0;
            setPage(1);
        }
    }, [filterQuery,scheduleDay]);

    useEffect(() => {
      if(userData && userData.savedFilters && userData.savedFilters.length > 0){
        setSavedSearchFilters(userData.savedFilters);
      }
    }, [userData]);

    // Add effect to initialize list view scrolling when data changes
    useEffect(() => {
        // Use setTimeout to ensure the DOM has updated with the data
        if (viewMode === 'list' && scrollContainerRef.current && carsData.length > 0) {
            setTimeout(() => {
                // Force a reflow/repaint for the list view
                const forceReflow = scrollContainerRef.current.offsetHeight;
                scrollContainerRef.current.style.overflowY = 'scroll';
                
                console.log('Initializing list view scroll with data:', {
                    carsCount: carsData.length,
                    containerHeight: scrollContainerRef.current.offsetHeight,
                    scrollHeight: scrollContainerRef.current.scrollHeight,
                    clientHeight: scrollContainerRef.current.clientHeight
                });
            }, 100);
        }
    }, [carsData, viewMode]);

    // Modify the effect to enforce uniform card sizes on mobile
    useEffect(() => {
        // Function to apply uniform heights
        const applyUniformCardSizes = () => {
            if (window.innerWidth <= 768 && viewMode === 'grid') {
                const cards = document.querySelectorAll(`.${classes.auctionCard}`);
                if (cards.length === 0) return;
                
                // Set fixed dimensions for all cards
                cards.forEach(card => {
                    // Main card container
                    card.style.height = '280px';
                    card.style.maxHeight = '280px';
                    card.style.minHeight = '280px';
                    card.style.overflow = 'hidden';
                    card.style.width = '100%';
                    card.style.boxSizing = 'border-box';
                    
                    // Image container
                    const imgContainer = card.querySelector(`.${classes.carImageContainer}`);
                    if (imgContainer) {
                        imgContainer.style.height = '120px';
                        imgContainer.style.minHeight = '120px';
                        imgContainer.style.maxHeight = '120px';
                        imgContainer.style.overflow = 'hidden';
                        imgContainer.style.borderRadius = '8px';
                        imgContainer.style.backgroundColor = '#f8f8f8';
                        
                        // Image itself
                        const img = imgContainer.querySelector('img');
                        if (img) {
                            img.style.height = '100%';
                            img.style.width = '100%';
                            img.style.objectFit = 'cover';
                            img.style.objectPosition = 'center';
                        }
                        
                        // Bid label
                        const bid = imgContainer.querySelector(`.${classes.Bid}`);
                        if (bid) {
                            bid.style.position = 'absolute';
                            bid.style.bottom = '0';
                            bid.style.right = '0';
                            bid.style.maxWidth = '90%';
                            bid.style.overflow = 'hidden';
                            bid.style.whiteSpace = 'nowrap';
                            bid.style.textOverflow = 'ellipsis';
                            
                            const bidText = bid.querySelector('p');
                            if (bidText) {
                                bidText.style.fontSize = '11px';
                                bidText.style.fontWeight = '600';
                            }
                        }
                    }
                    
                    // Text content area
                    const textContent = card.querySelector(`.${classes.cardTextContent}`);
                    if (textContent) {
                        textContent.style.height = '150px';
                        textContent.style.minHeight = '150px';
                        textContent.style.maxHeight = '150px';
                        textContent.style.overflow = 'hidden';
                        textContent.style.display = 'flex';
                        textContent.style.flexDirection = 'column';
                        textContent.style.padding = '0 2px';
                        
                        // Title
                        const title = textContent.querySelector('h3');
                        if (title) {
                            title.style.fontSize = '13px';
                            title.style.fontWeight = '600';
                            title.style.margin = '4px 0';
                            title.style.whiteSpace = 'nowrap';
                            title.style.overflow = 'hidden';
                            title.style.textOverflow = 'ellipsis';
                            title.style.maxHeight = '34px';
                    }
                    
                        // KM div
                        const kmDiv = textContent.querySelector(`.${classes.kmDiv}`);
                        if (kmDiv) {
                            kmDiv.style.marginTop = '0';
                            kmDiv.style.marginBottom = '4px';
                            
                            const kmText = kmDiv.querySelector('p');
                            if (kmText) {
                                kmText.style.fontSize = '12px';
                                kmText.style.lineHeight = '1.2';
                                kmText.style.padding = '2px 8px';
                            }
                        }
                    }
                    
                    // Details section
                    const details = card.querySelector(`.${classes.auctionCarDetails}`);
                    if (details) {
                        details.style.height = '76px';
                        details.style.minHeight = '76px';
                        details.style.maxHeight = '76px';
                        details.style.overflow = 'hidden';
                        details.style.marginTop = '6px';
                        
                        // Each text row
                        const textRows = details.querySelectorAll(`.${classes.auctionCarText}`);
                        textRows.forEach(row => {
                            row.style.height = '16px';
                            row.style.lineHeight = '16px';
                            row.style.marginBottom = '5px';
                            row.style.display = 'flex';
                            row.style.alignItems = 'center';
                            
                            // Hide text labels on mobile
                            const label = row.querySelector('p');
                            if (label) {
                                label.style.display = 'none';
                            }
                            
                            // Format value text
                            const text = row.querySelector('span');
                            if (text) {
                                text.style.fontSize = '12px';
                                text.style.lineHeight = '1.2';
                                text.style.whiteSpace = 'nowrap';
                                text.style.overflow = 'hidden';
                                text.style.textOverflow = 'ellipsis';
                                text.style.fontWeight = '500';
                            }
                            
                            // Icon container
                            const iconContainer = row.querySelector(`.${classes.IconContainer}`);
                            if (iconContainer) {
                                iconContainer.style.width = '18px';
                                iconContainer.style.height = '18px';
                                iconContainer.style.minWidth = '18px';
                                iconContainer.style.display = 'flex';
                                iconContainer.style.alignItems = 'center';
                                iconContainer.style.justifyContent = 'center';
                                iconContainer.style.marginRight = '6px';
                                
                                const icon = iconContainer.querySelector('i');
                                if (icon) {
                                    icon.style.fontSize = '10px';
                                }
                            }
                        });
                    }
                });
                
                console.log('Applied uniform card sizes to', cards.length, 'cards');
            }
        };
        
        // Apply immediately
        applyUniformCardSizes();
        
        // Apply after short delay to ensure all cards are loaded
        setTimeout(applyUniformCardSizes, 300);
        
        // Apply on resize with debounce
        let resizeTimer;
        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(applyUniformCardSizes, 100);
        };
        
        window.addEventListener('resize', handleResize);
        
        // Apply after data changes or view mode changes
        const observer = new MutationObserver(applyUniformCardSizes);
        if (scrollContainerRef.current) {
            observer.observe(scrollContainerRef.current, { childList: true, subtree: true });
        }
        
        // Clean up
        return () => {
            window.removeEventListener('resize', handleResize);
            observer.disconnect();
        };
    }, [classes, viewMode]);

    // Modify getCarsList to handle the data correctly
    const getCarsList = useCallback(() => {
            if (page === 1) {
                setInitialLoading(true);
                setPageSize(9); // Reset to 9 for initial load
            } else {
                setLoading(true);
                setPageSize(9); // Use 9 for subsequent loads
            }
            dispatch(setFilterQuery(filterString));
            
        // Add sort parameters to the API request - now backend handles all sorting consistently
        let sortParams = "";
        if (sortBy !== "default") {
            // Map the UI sort options to API sort parameters
            switch (sortBy) {
                case "km":
                    sortParams = "&sort=mileage:asc";
                    console.log("Applying km ascending sort");
                    break;
                case "kmDesc":
                    sortParams = "&sort=mileage:desc";
                    console.log("Applying km descending sort");
                    break;
                case "bidDate":
                    // Add combined date and time sort - first sort by date, then by time
                    sortParams = "&sort=bidDetail.bidDate:asc,auctionTime:asc";
                    break;
                case "bidDateDesc":
                    // Add combined date and time sort - first sort by date descending, then by time descending
                    sortParams = "&sort=bidDetail.bidDate:desc,auctionTime:desc";
                    break;
                case "grade":
                    // Backend now handles the custom grade sorting
                    sortParams = "&sort=inspectionScore:asc";
                    break;
                case "gradeDesc":
                    sortParams = "&sort=inspectionScore:desc";
                    break;
                case "bidNo":
                    sortParams = "&sort=bidNo:asc";
                    break;
                case "bidNoDesc":
                    sortParams = "&sort=bidNo:desc";
                    break;
                default:
                    sortParams = "";
            }
        }
            
        // GET /cars/search caps `search` at 64 characters (400 above that) and the value
        // has to be percent encoded, otherwise a term containing & or # corrupts the
        // rest of the query string.
        const safeSearchString = encodeURIComponent((searchString || "").slice(0, SEARCH_MAX_LENGTH));
        const payload = `pageNo=${page}&size=${pageSize}&search=${safeSearchString}${filterString}${sortParams}`;
        console.log("API request payload:", payload);

        // Capture the request id for this fetch. Only the response matching the
        // latest id is allowed to update state; earlier in-flight responses are stale.
        const requestId = ++latestRequestId.current;

        carSearch(payload).then((res) => {
            // Ignore stale responses (a newer request has since been issued)
            if (requestId !== latestRequestId.current) {
                console.log(`Ignoring stale response for request ${requestId} (latest is ${latestRequestId.current})`);
                return;
            }
            if (res?.message === "Success") {
                const newTotal = res?.data[0]?.total || 0;
                setTotalCars(newTotal);
                
                console.log(`Fetched page ${page}, got ${res?.data[0]?.data?.length || 0} items out of ${newTotal} total`);
                
                let newData = res?.data[0].data;
                
                // Log data for debugging
                if (newData && newData.length > 0) {
                    console.log("First few items:", newData.slice(0, 3).map(car => ({
                        id: car._id,
                        mileage: car.mileage,
                        maker: car.makerDetail?.maker,
                        model: car.model
                    })));
                } else {
                    console.log("No data returned from API");
                }
                
                // Filter out excluded makers
                newData = newData.filter(car => 
                    !EXCLUDED_MAKERS.includes(car.makerDetail?.maker)
                );
                
                // Add sortable timestamps to each car for more reliable sorting
                newData.forEach(car => {
                    // Create a sortable timestamp combining date and time
                    car._sortableTimestamp = createSortableTimestamp(car.bidDetail?.bidDate, car.auctionTime);
                });
                
                // Client-side fallback sorting for date and time
                if ((sortBy === "bidDate" || sortBy === "bidDateDesc") && newData && newData.length > 0) {
                    // Use the pre-calculated timestamps for sorting
                    newData.sort((a, b) => {
                        // Keep the server's USS-first grouping (one page can hold the last
                        // USS cars and the first of the rest) and order by time within it
                        if (Boolean(a.isUss) !== Boolean(b.isUss)) {
                            return a.isUss ? -1 : 1;
                        }
                        return sortBy === "bidDate" 
                            ? a._sortableTimestamp - b._sortableTimestamp
                            : b._sortableTimestamp - a._sortableTimestamp;
                    });
                    
                    // Log the sorted order for debugging
                    console.log("Sorted by date+time:", newData.slice(0, 5).map(car => ({
                        bidNo: car.bidNo,
                        date: car.bidDetail?.bidDate,
                        time: car.auctionTime,
                        timestamp: car._sortableTimestamp,
                        formatted: new Date(car._sortableTimestamp).toLocaleString()
                    })));
                }
                
                // Check if we actually got new data
                if (!newData || newData.length === 0) {
                    console.log("No new data received for this page, may have reached the end");
                    setInitialLoading(false);
                    setLoading(false);
                    
                    // If this is the first page and we got no results, make sure we show empty state
                    if (page === 1) {
                        setCarsData([]);
                        setInitialCarsData([]);
                    }
                    return;
                }
                
                if (page === 1) {
                    // For first page, just set the data directly
                    console.log("Setting initial data with", newData.length, "items");
                    setCarsData(newData);
                    setInitialCarsData(newData);
                    setInitialLoading(false);
                } else {
                    // Before appending, check for duplicates using car IDs
                    setCarsData(prevData => {
                        // Get current car IDs to check for duplicates
                        const existingIds = new Set(prevData.map(car => car._id));
                        // Filter out any cars we already have
                        const uniqueNewCars = newData.filter(car => !existingIds.has(car._id));
                        
                        console.log(`Appending ${uniqueNewCars.length} unique new cars to existing ${prevData.length}`);
                        
                        // If no unique new cars, don't update state
                        if (uniqueNewCars.length === 0) {
                            return prevData;
                        }
                        
                        return [...prevData, ...uniqueNewCars];
                    });
                }
                
                // Always ensure loading states are reset
                setInitialLoading(false);
                setLoading(false);
            } else {
                // Handle error case
                setInitialLoading(false);
                setLoading(false);
                console.error("Error fetching car data:", res);
                
                // If this is the first page and we got an error, make sure we show empty state
                if (page === 1) {
                    setCarsData([]);
                    setInitialCarsData([]);
                }
            }
        }).catch(error => {
            // Ignore stale errors (a newer request has since been issued)
            if (requestId !== latestRequestId.current) {
                console.log(`Ignoring stale error for request ${requestId} (latest is ${latestRequestId.current})`);
                return;
            }
            setInitialLoading(false);
            setLoading(false);
            console.error("API call failed:", error);

            // If this is the first page and we got an error, make sure we show empty state
            if (page === 1) {
                setCarsData([]);
                setInitialCarsData([]);
            }
        });
    }, [page, searchString, filterString, pageSize, dispatch, sortBy]);

    useEffect(() => {
        // Only fetch if we have a valid page number
        if (page > 0) {
            getCarsList();
        }
    }, [getCarsList, page]);

    // Back to the first car. The desktop grid scrolls with the page, so that means bringing
    // its heading back out from under the navbar (scroll-margin-top in desktop.scss) if the
    // page has gone past it; a page still above the results stays where it is.
    const scrollToFirstCar = () => {
        const results = scrollContainerRef.current;
        if (!results) return;
        results.scrollTop = 0;
        const container = results.parentElement;
        if (!isMobile && container.getBoundingClientRect().top < parseFloat(getComputedStyle(container).scrollMarginTop)) {
            container.scrollIntoView({ block: 'start' });
        }
    };

    // Add a separate effect to reset pagination when sort changes
    useEffect(() => {
        // When sort option changes, reset to page 1 and scroll to top
        scrollToFirstCar();
        
        // Clear any existing data first to avoid mixing differently sorted items
        setCarsData([]);
        
        // Then reset page to 1
        setPage(1);
        
        // Don't directly call getCarsList here - it will be triggered by the page change
        // This prevents duplicate API calls
        console.log("Sort changed to", sortBy, "- resetting pagination");
    }, [sortBy]);

    // Separate effect for search and filter changes
    useEffect(() => {
        // Reset to page 1 when search or filter changes
        scrollToFirstCar();
        
        // Clear any existing data first
        setCarsData([]);
        
        // Then reset page
        setPage(1);
        
        console.log("Search or filter changed - resetting pagination");
    }, [searchString, filterString, sortBy]);


    const onClickFavouriteHandler = (content) => {
      // Store current favorite state before update
      const wasAlreadyFavorite = content.isFavorite || localFavorites.has(content._id);
      
      // Optimistic update
      setLocalFavorites(prev => {
          const newSet = new Set(prev);
          if (newSet.has(content._id)) {
              newSet.delete(content._id);
          } else {
              newSet.add(content._id);
          }
          return newSet;
      });
      
      // Update Redux store for persistence across different pages
      dispatch(updateCarDetail({ 
        _id: content._id,
        isFavorite: !wasAlreadyFavorite 
      }));

      // API call
      updateCarFavourite({ carId: content?._id }).then((res) => {
          if (res.status !== 201) {
              // Revert on failure
              setLocalFavorites(prev => {
                  const newSet = new Set(prev);
                  if (wasAlreadyFavorite) {
                      newSet.add(content._id);
                  } else {
                      newSet.delete(content._id);
                  }
                  return newSet;
              });
              
              // Also revert in Redux
              dispatch(updateCarDetail({ 
                _id: content._id,
                isFavorite: wasAlreadyFavorite 
              }));
              
              // Show error notification
              displayErrorSnack("Failed to update favorite");
          }
      }).catch(() => {
          // Revert on error
          setLocalFavorites(prev => {
              const newSet = new Set(prev);
              if (wasAlreadyFavorite) {
                  newSet.add(content._id);
              } else {
                  newSet.delete(content._id);
              }
              return newSet;
          });
          
          // Also revert in Redux
          dispatch(updateCarDetail({ 
            _id: content._id,
            isFavorite: wasAlreadyFavorite 
          }));
          
          // Show error notification
          displayErrorSnack("Failed to update favorite");
      });
    };

    const onClickFilterHandler = () => {
        if(isMobile){
            // Set animation state first
            if (isMobileFilterOpen) {
                setFilterAnimationState('closing');
                // Short delay to allow animation to play
                setTimeout(() => {
                    dispatch(setMobileFilterOpen(false));
                    setFilterAnimationState('');
                    // Enable scrolling on body when filter is closed
                    document.body.style.overflow = 'auto';
                }, 280);
            } else {
                dispatch(setMobileFilterOpen(true));
                setFilterAnimationState('opening');
                setTimeout(() => setFilterAnimationState(''), 300);
                // Disable scrolling on body when filter is open to prevent background scrolling
                document.body.style.overflow = 'hidden';
            }
        } else {
            // Set animation state first
            if (isDesktopFilterOpen) {
                setFilterAnimationState('closing');
                // Short delay to allow animation to play
                setTimeout(() => {
                    dispatch(setDesktopFilterOpen(false));
                    setFilterAnimationState('');
                }, 280);
            } else {
                dispatch(setDesktopFilterOpen(true));
                setFilterAnimationState('opening');
                setTimeout(() => setFilterAnimationState(''), 300);
            }
        }
    }

    const filterHandler = (filterString) => {
      // No year clamp here: it used to re-apply 1960-2003 when filters were cleared under
      // the Km sort, which hid everything newer (Ferrari/Lamborghini run to 2005) and, being
      // sent as strings, the API read it as "exactly 1960 or 2003" rather than a range. The
      // API applies its own year rule either way.
      setFilterString(filterString);
    };

    useEffect(() => {
      if (selectedSavedSearchFilter.name !== '' && selectedSavedSearchFilter.filter !== '') { 
        setFilterString(selectedSavedSearchFilter.filter);
      }
    }, [selectedSavedSearchFilter]);

    // A saved search is its filters plus the search-bar text, so applying one replaces
    // both and drops any auction-schedule pick. filterQuery is set alongside: the
    // schedule effect above re-applies it whenever the day is cleared.
    const applySavedSearch = (savedSearch) => {
      if (scheduleDay || scheduleSite.length > 0) {
        dispatch(setDay(''));
        dispatch(setSites([]));
      }
      dispatch(setFilterQuery(savedSearch.filter || ''));
      dispatch(setSearchQuery(savedSearch.search || ''));
      setFilterString(savedSearch.filter || '');
      setSelectedSavedSearchFilter({ ...savedSearch });
    };

    const clearSearchText = () => {
      if (scheduleSite.length > 0) {
        dispatch(setSites([]));
      }
      dispatch(setSearchQuery(''));
    };


    const clearCarList = () => {
      setTotalCars(0);
      setCarsData([]);
    };

    // GET /cars/filters is rate limited per minute and this effect refires on every
    // change of filterString - i.e. once per checkbox tick in the maker/colour/year
    // accordion. Wait for the clicking to pause before asking the server, and skip the
    // call entirely when the resulting payload is the one we already have.
    const lastFilterPayload = useRef(null);

    useEffect(() => {
      const payload = `!${filterString}`.replace("!&", "").replace("!", "");
      if (lastFilterPayload.current === payload) {
        return;
      }

      const timer = setTimeout(() => {
        lastFilterPayload.current = payload;
        getCarFilters(payload).then((res) => {
          console.log("Filters response:", res.data);

          // Debug all model-related data
          console.log("Model filters:", res.data?.model);
          console.log("MakerDetail.type filters:", res.data?.["makerDetail.type"]);
          console.log("MakerModels mapping:", res.data?.makerModels);

          // Explicitly check for the structure
          if (res.data?.makerModels) {
            console.log("Found makerModels mapping - makers:", Object.keys(res.data.makerModels));
            // Sample the first maker to see its models
            const firstMaker = Object.keys(res.data.makerModels)[0];
            if (firstMaker) {
              console.log(`Models for ${firstMaker}:`, res.data.makerModels[firstMaker]);
            }
          } else if (res.data?.model) {
            console.log("Using generic model list - count:", res.data.model.length);
          } else if (res.data?.["makerDetail.type"]) {
            console.log("Using makerDetail.type - count:", res.data["makerDetail.type"].length);
          }

          if (res.message === "Success") {
            setCarFiltersList(res?.data);
          }
        }).catch(error => {
          // Let the next filter change retry (this payload was never fetched).
          lastFilterPayload.current = null;
          console.error("Error fetching filters:", error);
        });
      }, 400);

      return () => clearTimeout(timer);
    }, [filterString]);
    console.log("Filter String", filterString);
    console.log("Car Filters List", carFiltersList);

    const onCardClickHandler = (carDetail) => {
      // Log car details for debugging
      console.log("AuctionContent: Opening car detail with ID:", carDetail._id);
      console.log("AuctionContent: Car Bid No:", carDetail.bidNo);
      
      dispatch(setCarDetail(carDetail));
      // Include the current location pathname to return to this page
      const detailUrl = `/detail/${carDetail._id}`;
      // Create a URL object with state to open in new tab
      const url = new URL(window.location.origin + detailUrl);
      // Add a custom query parameter to store the return path
      url.searchParams.set('returnTo', window.location.pathname + window.location.search);
      // Open in new tab with the return path information
      window.open(url.toString(), '_blank');
    };
    const getCarName=(content)=>{
    let vehicleName = content.model + " " + content.makerDetail.maker + " " +  content.makerDetail.model + " " + (content.makerDetail.grade ? content.makerDetail.grade : "");
    return vehicleName;
    }

    // Create a common scroll handler that can be used for both grid and list views
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isScrollingDown = scrollTop > (scrollContainerRef.current?.lastScrollTop || 0);
        scrollContainerRef.current.lastScrollTop = scrollTop;
        
        // Log the scroll information for debugging
        const currentView = viewMode === 'grid' ? 'grid' : 'list';
        console.log(`Scrolling in ${currentView} view:`, {
            scrollTop,
            scrollHeight,
            clientHeight,
            isScrollingDown,
            isNearBottom: scrollHeight - scrollTop - clientHeight <= clientHeight * 0.2,
            hasScroll: scrollHeight > clientHeight
        });
        
        // Make bottom detection more generous - check if we're within 20% of the bottom
        const isNearBottom = scrollHeight - scrollTop - clientHeight <= clientHeight * 0.2;
        
        // Force a height check to ensure we're detecting scroll properly
        if (currentView === 'list' && !scrollContainerRef.current.heightChecked) {
            console.log('Checking list view height...', {
                containerScrollHeight: scrollHeight,
                containerClientHeight: clientHeight,
                containerHasScroll: scrollHeight > clientHeight,
                actualContentHeight: scrollContainerRef.current.scrollHeight
            });
            scrollContainerRef.current.heightChecked = true;
        }
        
        // Only check for loading more items if we're scrolling down and near the bottom
        // Also verify we're not already at the end of all available data
        if (isScrollingDown && isNearBottom && !loading && carsData.length < totalCars) {
            loadNextPage();
        }
    };

    const loadNextPage = () => {
        // Add a small delay to prevent multiple triggers
        if (!scrollContainerRef.current.isLoadingScheduled) {
            const container = scrollContainerRef.current;
            container.isLoadingScheduled = true;
            
            console.log('Near bottom, loading more cars...', {
                currentPage: page,
                carsLoaded: carsData.length,
                totalAvailable: totalCars,
                maxPages: Math.ceil(totalCars / totalCarsToShow)
            });
            
            // Use setTimeout to create a small delay before loading the next page
            setTimeout(() => {
                setPage(prevPage => {
                    // Double-check that we haven't already increased the page
                    // and that we haven't loaded all available cars yet
                    const maxPage = Math.ceil(totalCars / totalCarsToShow);
                    if (prevPage < maxPage && carsData.length < totalCars) {
                        console.log(`Incrementing page from ${prevPage} to ${prevPage + 1}`);
                        return prevPage + 1;
                    }
                    console.log(`Not incrementing page: at ${prevPage}/${maxPage}, loaded ${carsData.length}/${totalCars} cars`);
                    return prevPage;
                });
                // Reset the loading scheduled flag
                container.isLoadingScheduled = false;
            }, 200); // Increased delay for more stability
        }
    };

    // The desktop grid scrolls with the page, so its next cars load when its end comes within
    // 20% of the window's height of the window's bottom. Scroll events don't bubble; capturing
    // them on the window sees the <body>, which is what scrolls here (index.scss). Checking on
    // subscribe as well covers a window tall enough that the first cars leave nothing to scroll.
    useEffect(() => {
        if (isMobile || viewMode !== 'grid') return undefined;
        const loadWhenNearEnd = () => {
            const grid = scrollContainerRef.current;
            if (!grid || loading || initialLoading || carsData.length === 0 || carsData.length >= totalCars) return;
            if (grid.getBoundingClientRect().bottom - window.innerHeight <= window.innerHeight * 0.2) {
                loadNextPage();
            }
        };
        loadWhenNearEnd();
        window.addEventListener('scroll', loadWhenNearEnd, { capture: true, passive: true });
        return () => window.removeEventListener('scroll', loadWhenNearEnd, { capture: true });
    }, [isMobile, viewMode, loading, initialLoading, carsData.length, totalCars]);

    // --- Props for child components --- 
    const commonProps = {
      classes,
      carsData,
      loading,
      initialLoading,
      totalCars,
      page,
      setPage,
      viewMode,
      handleViewModeChange,
      sortBy,
      setSortBy,
      onCardClickHandler,
      onClickFavouriteHandler,
      getCarName,
      localFavorites,
      scrollContainerRef,
      handleScroll,
      userData,
      isFreeTier,
      searchText: searchString || '',
      onClearSearch: clearSearchText,
      onApplySavedSearch: applySavedSearch
    };

    const desktopProps = {
      ...commonProps,
      muiTheme,
      filterAnimationState,
      isDesktopFilterOpen,
      onClickFilterHandler,
      selectedSavedSearchFilter,
      setSelectedSavedSearchFilter,
      savedSearchFilters,
      setSavedSearchFilters,
      filterString,
      filterHandler,
      clearCarList,
      carFiltersList,
      scheduleDay,
      filterParameters, // Ensure this is defined or remove if not needed for Desktop
    };
    
    const mobileProps = {
        ...commonProps,
        isMobileFilterOpen, // Pass mobile filter state
        onClickFilterHandler, // Pass filter toggle handler
        // Filter related props
        muiTheme,
        filterAnimationState,
        selectedSavedSearchFilter,
        setSelectedSavedSearchFilter,
        savedSearchFilters,
        setSavedSearchFilters,
        filterString,
        filterHandler,
        clearCarList,
        carFiltersList,
        scheduleDay,
    };

    // Add the missing displayErrorSnack function
    const displayErrorSnack = (message) => {
        enqueueSnackbar(message, {
            variant: 'error',
            autoHideDuration: 3000,
        });
    };

    return (
      <>
        {/* Conditionally render based on isMobile */}
        {isMobile ? (
          <MobileAuctionContent {...mobileProps} />
        ) : (
          <DesktopAuctionContent {...desktopProps} />
                                )}
                            </>
    );
}

export default AuctionContent;


let filterParameters = [
    {
      key: "maker_name",
      icon: 'fas fa-car',
      label: "Make",
      options: ['BMW','EUNOS','HONDA','MAZDA','MITSUBISHI','NISSAN','PORSCHE','SUBARU','SUZUKI','TOYOTA'],
    },
    {
      key: "model",  // Leave as "model" for backend compatibility
      modelKey: "makerDetail.type", // Use this key when accessing data in the frontend
      icon: 'fas fa-box',
      label: "Model",
      options: [] // Empty array to be populated by API data
    },
    {
      key: "grade",
      icon: 'far fa-star',
      label: "Grade",
      options: [4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1]
    },
    {
      key: "year",
      icon: 'far fa-calendar',
      label: "Year",
      options: [1960, 2024]
    },
    {
      key: "color",
      icon: 'fas fa-palette',
      label: "Color",
      options: ['#000', '#4E5BA6', '#47CD89', '#FF0000', '#FDB022', '#669F2A', '#F9DBAF', 'Orange', 'Purple', 'Pink']
    },
    {
      key: "transmission",
      icon: 'fas fa-cog',
      label: "Transmission",
      options: ['Automatic', 'Manual']
    },
    {
      key: "bidDate",
      icon: 'far fa-calendar-alt',
      label: "Bid Date",
      options: ['28/09/2024', '29/09/2024', '30/09/2024', '01/10/2024', '02/10/2024', '03/10/2024', '04/10/2024', '05/10/2024', '06/10/2024', '07/10/2024']
    }
  ];